import type { JsonObject, TimestampMillis } from './common';

export const AUDIT_ACTIONS = [
  'project.created',
  'project.updated',
  'project.archived',
  'script.created',
  'script_version.created',
  'script_version.published',
  'release.published',
  'release.yanked',
  'license.issued',
  'license.bulk_issued',
  'license.activated',
  'license.suspended',
  'license.reinstated',
  'license.revoked',
  'license.session_started',
  'license.session_ended',
  'checkpoint.session_started',
  'checkpoint.step_verified',
  'checkpoint.completed',
  'checkpoint.failed',
  'customer.created',
  'order.created',
  'order.paid',
  'refund.requested',
  'refund.settled',
  'subscription.changed',
  'build.started',
  'build.finished',
  'artifact.downloaded',
  'member.invited',
  'role.changed',
  'api_key.created',
  'api_key.revoked',
  'auth.failed',
] as const;

export type AuditAction = (typeof AUDIT_ACTIONS)[number];

export const SECURITY_EVENT_TYPES = [
  'credential.replay',
  'credential.brute_force',
  'signature.mismatch',
  'checkpoint.forged',
  'license.secret.reuse',
  'rbac.denied',
  'rate_limit.exceeded',
  'api_key.leak_suspected',
] as const;

export type SecurityEventType = (typeof SECURITY_EVENT_TYPES)[number];

export const SECURITY_EVENT_SEVERITIES = ['low', 'medium', 'high', 'critical'] as const;

export type SecurityEventSeverity = (typeof SECURITY_EVENT_SEVERITIES)[number];

export const OUTBOX_STATUSES = [
  'pending',
  'dispatched',
  'published',
  'failed',
  'discarded',
] as const;

export type OutboxStatus = (typeof OUTBOX_STATUSES)[number];

export const OUTBOX_EVENT_TYPES = [
  'license.issued',
  'license.revoked',
  'checkpoint.completed',
  'order.paid',
  'build.finished',
] as const;

export type OutboxEventType = (typeof OUTBOX_EVENT_TYPES)[number];

export interface AuditLog {
  id: string;
  organization_id: string;
  actor_id: string;
  actor_kind: 'session' | 'api_key' | 'system';
  action: AuditAction;
  target_type: string;
  target_id: string;
  request_id: string;
  ip_mask: string | null;
  recorded_at: TimestampMillis;
  payload: JsonObject;
}

export interface SecurityEvent {
  id: string;
  organization_id: string | null;
  type: SecurityEventType;
  severity: SecurityEventSeverity;
  subject_type: string;
  subject_id: string;
  request_id: string;
  observed_at: TimestampMillis;
  details: JsonObject;
  resolved_at: TimestampMillis | null;
}

export interface OutboxEvent {
  id: string;
  organization_id: string;
  type: OutboxEventType;
  aggregate_type: string;
  aggregate_id: string;
  status: OutboxStatus;
  attempt_count: number;
  available_at: TimestampMillis;
  created_at: TimestampMillis;
  dispatched_at: TimestampMillis | null;
  published_at: TimestampMillis | null;
  payload: JsonObject;
  last_error: string | null;
}

export function isAuditAction(value: string): value is AuditAction {
  return (AUDIT_ACTIONS as readonly string[]).includes(value);
}

export function isSecurityEventType(value: string): value is SecurityEventType {
  return (SECURITY_EVENT_TYPES as readonly string[]).includes(value);
}

export function isOutboxStatus(value: string): value is OutboxStatus {
  return (OUTBOX_STATUSES as readonly string[]).includes(value);
}

export function isOutboxEventType(value: string): value is OutboxEventType {
  return (OUTBOX_EVENT_TYPES as readonly string[]).includes(value);
}

export function outboxDue(
  event: Pick<OutboxEvent, 'status' | 'available_at'>,
  reference: TimestampMillis,
): boolean {
  return event.status === 'pending' && event.available_at <= reference;
}
